import { Button, Popover, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import type { TeamMember } from "../types";
import { TrackOnClick, postUserAction } from "../../host/TrackOnClick";
import { useResendVerificationEmail } from "../hooks/useResendVerificationEmail";
import styles from "./EmailVerificationBadge.module.css";

type VerificationFields = Pick<
  TeamMember,
  "email" | "isEmailVerified" | "isScimUserSynced"
>;

// Dark AhaSlides tooltip background (storybook palette indigo.90), so the
// popover/tooltip match the presenter's dark toggletip instead of AntD's default
// white. White copy + the green CTA (styles.line / styles.action) sit on top.
const TOOLTIP_DARK_BG = "#242442";

// Whether an unverified-email badge belongs on this member's row. Callers gate
// on this (MembersTable) so the badge component — and, on the own row, its
// resend hook — never mounts for a verified / SCIM / no-email member.
//
// A member is verified when general-api says so (isEmailVerified — SSO folded in
// upstream) OR their account is SCIM-provisioned. The badge shows only for the
// unverified case, and only when general-api EXPLICITLY says so: strict
// `=== false`, so a row missing the field (before general-api ships it) shows
// nothing rather than a false warning. Mirrors the presenter's shouldShowVerifyBadge.
//
// Two independent SCIM signals both mean "verified by the IdP", so either one
// suppresses the badge:
//   - scimEnabled — account-level (host flag = the presenter's isScimEnabled,
//     "the account has an enabled SCIM key"). This is the signal that fixed the
//     gawin1 mismatch: a SCIM-account member who was never individually synced.
//   - member.isScimUserSynced — per-user (account/v2), stricter. Left in the OR
//     so the badge stays correct even if the host scimEnabled flag is ever absent.
export function shouldShowVerificationBadge(
  member: VerificationFields,
  scimEnabled: boolean,
): boolean {
  if (!member.email) return false;
  if (scimEnabled || member.isScimUserSynced) return false;
  return member.isEmailVerified === false;
}

// Emit hover_email_verification_badge — the same Mixpanel event the presenter
// fires from the badge's v-aha-emit-action.hover, so the funnel is unified. The
// host maps the DOM event `mouseenter` -> the `hover` action; only unverified
// badges ever render, hence the constant status.
function emitBadgeHover(interactive: boolean) {
  postUserAction("mouseenter", "email-verification-badge", {
    email_verification_status: "unverified",
    is_interactive: interactive,
  });
}

// system-shield-warning, the exact design glyph from stpancras-storybook-app
// (added for AHA-39565: src/assets/icons/system-shield-warning.svg). Two
// currentColor strokes — the shield + exclamation stem, and the dot — so the
// wrapper's warning colour drives the whole icon.
function ShieldWarningIcon() {
  // Rendered at 12×12 per the design; viewBox stays 16 so the glyph's paths
  // scale down rather than clip.
  return (
    <svg width={12} height={12} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M8.00034 7.71744V4.89136M14.696 5.86957V1.6087C14.696 1.44726 14.6319 1.29244 14.5177 1.17828C14.4036 1.06413 14.2487 1 14.0873 1H1.91338C1.75195 1 1.59712 1.06413 1.48297 1.17828C1.36882 1.29244 1.30469 1.44726 1.30469 1.6087V5.86957C1.30469 13.1739 8.00034 15 8.00034 15C8.00034 15 14.696 13.1739 14.696 5.86957Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 10.5L8 10.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface EmailVerificationBadgeProps {
  // The signed-in user's own row gets the interactive Verify now / Resend
  // popover; everyone else gets the read-only "tell your team member" tooltip.
  // Callers MUST gate on shouldShowVerificationBadge — this always renders a badge.
  interactive?: boolean;
}

export function EmailVerificationBadge({
  interactive = false,
}: EmailVerificationBadgeProps) {
  const { t } = useTranslation();

  // Own row → the interactive variant, which owns the resend hook. Split out so
  // read-only (and never-mounted verified) rows don't pay for that hook.
  if (interactive) {
    return <InteractiveVerificationBadge ariaLabel={t("team.emailVerification.aria")} />;
  }

  // Read-only rows: informational tooltip, hover-only, non-focusable (fine — no
  // action to reach).
  return (
    <Tooltip title={t("team.emailVerification.teamMemberHint")} color={TOOLTIP_DARK_BG}>
      <span
        className={styles.badge}
        data-testid="email-verification-badge"
        aria-label={t("team.emailVerification.aria")}
        onMouseEnter={() => emitBadgeHover(false)}
      >
        <ShieldWarningIcon />
      </span>
    </Tooltip>
  );
}

// Own-row variant: the resend hook plus the Verify now / Resend popover. Mounted
// only for the signed-in user's row, so the hook runs once — not once per member.
function InteractiveVerificationBadge({ ariaLabel }: { ariaLabel: string }) {
  const { t } = useTranslation();
  const { pending, hasRequested, resend } = useResendVerificationEmail();

  // After a successful send the copy switches to the "didn't receive it? resend"
  // state.
  const content = hasRequested ? (
    <div className={styles.popover}>
      <p className={styles.line}>{t("team.emailVerification.sent")}</p>
      <p className={styles.line}>{t("team.emailVerification.didntReceive")}</p>
      {/* click_email_verification_resend — identical to the presenter's
          v-aha-click name so it lands as one unified Mixpanel event. */}
      <TrackOnClick name="email_verification_resend">
        <Button
          type="link"
          size="small"
          className={styles.resend}
          loading={pending}
          onClick={() => void resend()}
          data-testid="email-verification-resend-button"
        >
          {t("team.emailVerification.resend")}
        </Button>
      </TrackOnClick>
    </div>
  ) : (
    <div className={styles.popover}>
      <p className={styles.line}>{t("team.emailVerification.selfPrompt")}</p>
      {/* click_email_verification_verify_now — matches the presenter. */}
      <TrackOnClick name="email_verification_verify_now">
        <Button
          type="primary"
          size="small"
          className={styles.action}
          loading={pending}
          onClick={() => void resend()}
          data-testid="email-verification-verify-now-button"
        >
          {t("team.emailVerification.verifyNow")}
        </Button>
      </TrackOnClick>
    </div>
  );

  // This popover is the user's own account-security action, so it must be
  // reachable by every input: hover (mouse), click (touch/tablet — hover never
  // fires there), and focus (keyboard). The trigger is a focusable role="button"
  // span so a keyboard user can Tab to it and open the popover; a plain span
  // takes no focus. (PR #20 review.)
  return (
    <Popover
      content={content}
      color={TOOLTIP_DARK_BG}
      trigger={["hover", "click", "focus"]}
      placement="top"
    >
      <span
        className={styles.badge}
        data-testid="email-verification-badge"
        aria-label={ariaLabel}
        role="button"
        tabIndex={0}
        onMouseEnter={() => emitBadgeHover(true)}
      >
        <ShieldWarningIcon />
      </span>
    </Popover>
  );
}
